import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { X, CheckCircle, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { useTranslation } from '../../i18n.ts';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  action?: ToastAction;
  duration?: number;
}

interface Toast {
  id: number;
  message: string;
  type: ToastType;
  action?: ToastAction;
  duration?: number;
}

interface ToastContextProps {
  toast: (message: string, type?: ToastType, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

let nextId = 0;

/**
 * #1825 — de onderbalken van de touch-schermen en de knoppenvoet van een venster (ModalFooter,
 * snelmenu); de melding staat erboven (styles.css), nooit over Annuleren/Opslaan.
 */
const ONDERBALKEN =
  '.pt-toev__voet, .pt-fam__voet, .pt-balk, .pe-touch .pe-modal-footer, .pe-event-modal-voet, ' +
  '.modal-footer, .qa-actions';
/**
 * Afstand van de onderrand van het venster tot de bovenkant van de hoogste zichtbare onderbalk.
 * Alleen een balk in het onderste derde telt: de voet van een klein, gecentreerd venster ligt
 * niet onder de melding, en dan hoeft die niet het venster in.
 */
// ponytail: vaste grens (2/3 van de hoogte); meet de toasthoogte als dat ooit te grof blijkt.
const onderbalkHoogte = () =>
  Math.max(
    0,
    ...[...document.querySelectorAll<HTMLElement>(ONDERBALKEN)]
      .filter((el) => el.getClientRects().length > 0)
      .map((el) => el.getBoundingClientRect().top)
      .filter((top) => top > (window.innerHeight * 2) / 3)
      .map((top) => window.innerHeight - top)
  );

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const doosRef = useRef<HTMLDivElement>(null);
  // Bij elke nieuwe melding opnieuw meten: welke balk er staat, wisselt per scherm.
  useLayoutEffect(() => {
    if (toasts.length)
      doosRef.current?.style.setProperty('--pt-toast-onder', `${Math.round(onderbalkHoogte())}px`);
  }, [toasts.length]);

  const addToast = useCallback(
    (message: string, type: ToastType = 'success', options?: ToastOptions) => {
      const id = nextId++;
      setToasts((prev) => [
        ...prev,
        { id, message, type, action: options?.action, duration: options?.duration },
      ]);
    },
    []
  );

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast: addToast }}>
      {children}
      {/* #1935 — z-[10002]: boven het aktescherm (9999) en zijn vergrotingen (10000/10001).
          pointer-events-none op de container voorkomt dat de fixed-positioned
          wrapper clicks blokkeert op elementen eronder (Save/Close knoppen in
          modals die rechtsboven uitkomen). pointer-events-auto op elke toast
          herstelt de klikbaarheid van de toast zelf (close-X, action-button). */}
      <div
        ref={doosRef}
        className="pt-toasts fixed top-4 right-4 z-[10002] flex flex-col gap-2 max-w-sm pointer-events-none"
        aria-live="polite"
        aria-atomic="true"
      >
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={() => removeToast(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const icons: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />,
  error: <AlertCircle className="w-4 h-4 text-pt-ruby shrink-0" />,
  warning: <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />,
  info: <Info className="w-4 h-4 text-blue-600 shrink-0" />,
};

const bgColors: Record<ToastType, string> = {
  success: 'bg-emerald-50 border-emerald-200',
  error: 'bg-pt-ruby-tint border-pt-ruby/20',
  warning: 'bg-amber-50 border-amber-200',
  info: 'bg-blue-50 border-blue-200',
};

const ToastItem: React.FC<{ toast: Toast; onDismiss: () => void }> = ({ toast, onDismiss }) => {
  const { t } = useTranslation();
  useEffect(() => {
    // Error toasts are sticky (no auto-dismiss) unless explicitly given a duration
    const duration = toast.duration ?? (toast.type === 'error' ? 0 : 4000);
    if (!duration) return; // sticky — user must dismiss manually
    const timer = setTimeout(onDismiss, duration);
    return () => clearTimeout(timer);
  }, [toast.duration, toast.type, onDismiss]);

  return (
    <div
      className={`pointer-events-auto flex items-start gap-2 p-3 rounded-xl border shadow-lg animate-in slide-in-from-right ${bgColors[toast.type]}`}
      role={toast.type === 'error' ? 'alert' : 'status'}
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
      aria-atomic="true"
    >
      {icons[toast.type]}
      <p className="text-xs font-medium text-pt-forest flex-1 leading-snug">{toast.message}</p>
      {toast.action && (
        <button
          type="button"
          onClick={() => {
            toast.action!.onClick();
            onDismiss();
          }}
          className="text-xs font-semibold text-pt-gold hover:text-pt-gold/80 underline underline-offset-2 shrink-0 whitespace-nowrap"
        >
          {toast.action.label}
        </button>
      )}
      <button
        onClick={onDismiss}
        className="text-pt-sage-text hover:text-pt-forest shrink-0"
        aria-label={t('close')}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};
